/**
 * The backups of the person's own sandbox, kept by the sandbox gateway (vfs-service src/gateway/myBackups.js) under
 * the vfs path the web app already uses, with the person's authorized fetch. Only ever their own sandbox's:
 * - `GET /api/v1/me/backups` -> `{ enabled, configured, sandboxName, state, live, limits: { manualPerDay,
 *   restoresPerDay, keepLast, keepDays }, used: { backups, restores }, backups, restores, active }` (or
 *   `{ enabled: false, reason }`);
 * - `POST /api/v1/me/backups` `{ name? }` -> the same with `run` ("Back up now");
 * - `GET /api/v1/me/backups/:id/projects` -> `{ id, projects: [{ id, name }] }`;
 * - `POST /api/v1/me/backups/:id/restore` `{ confirm, parts? | projects? }` -> the same as GET with `run`;
 * - `GET /api/v1/me/backups/:id/download?project=` -> a zip, saved as a file (fetch and blob: the auth header);
 * - `POST /uploads` `{ filename, size }`, `PUT /uploads/:id/parts/:n` (octet-stream), `POST /uploads/:id/complete`,
 *   `DELETE /uploads/:id`: "Restore from a zip…", stored as one more backup (madeBy 'upload') then restored as any.
 * An error carries `status` and the gateway's `code` (e.g. 'confirm_mismatch'). A gateway without these routes
 * (older, or shared mode: 404) resolves the list to null.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const url = (path = '') => `${base()}/api/v1/me/backups${path}`;
const seg = (value) => encodeURIComponent(String(value));

async function failure(response, fallback) {
  const data = await response.json().catch(() => null);
  const error = new Error(data?.error || `${fallback} (${response.status})`);
  error.status = response.status;
  if (data?.code) { error.code = data.code; }
  return error;
}

async function call(path, { method = 'GET', body } = {}) {
  if (!base()) { return null; }
  const response = await getAuthorizedFetch()(url(path), {
    method,
    headers: { Accept: 'application/json', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 404 && method === 'GET' && path === '') { return null; }
  if (!response.ok) { throw await failure(response, 'Backups failed'); }
  return response.json();
}

/** The person's backups, restores and limits; null where the gateway has none. */
export function getMyBackups() {
  return call('');
}

/** "Back up now", with an optional name. */
export function backUpNow(name = '') {
  const trimmed = String(name || '').trim();
  return call('', { method: 'POST', body: trimmed ? { name: trimmed } : {} });
}

export function getBackupProjects(backupId) {
  return call(`/${seg(backupId)}/projects`);
}

/** `{ confirm, projects? }`: everything without projects, else only those projects' code. */
export function restoreMyBackup(backupId, { confirm, projects = null } = {}) {
  return call(`/${seg(backupId)}/restore`, { method: 'POST', body: { confirm, ...(projects ? { projects } : {}) } });
}

/** The server's file name (Content-Disposition), else `fallback`. */
function fileNameOf(response, fallback) {
  const header = response.headers?.get?.('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match ? match[1] : fallback;
}

/** Downloads the zip of a backup's projects (`project`: one), as a file. */
export async function downloadMyBackup(backupId, project = null) {
  const response = await getAuthorizedFetch()(url(`/${seg(backupId)}/download${project ? `?project=${seg(project)}` : ''}`), { headers: { Accept: 'application/zip' } });
  if (!response.ok) { throw await failure(response, 'The download failed'); }
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileNameOf(response, `backup-${backupId}.zip`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10000);
}

// ---- Restore from a zip: the upload, in chunks (the gateway streams each one to the backups bucket) ----

const RETRY_WAITS_MS = [1000, 3000];
const wait = (ms, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) { reject(abortError()); return; }
  const timer = setTimeout(resolve, ms);
  signal?.addEventListener?.('abort', () => { clearTimeout(timer); reject(abortError()); }, { once: true });
});
function abortError() {
  const error = new Error('The upload was cancelled.');
  error.name = 'AbortError';
  return error;
}

/** One chunk, retried after 1 s and 3 s (3 tries in all); a refusal (4xx) is not retried. */
async function sendChunk(id, number, blob, { signal, waits = RETRY_WAITS_MS } = {}) {
  let lastError = null;
  for (let attempt = 0; attempt <= waits.length; attempt += 1) {
    if (signal?.aborted) { throw abortError(); }
    try {
      const response = await getAuthorizedFetch()(url(`/uploads/${seg(id)}/parts/${number}`), {
        method: 'PUT',
        headers: { Accept: 'application/json', 'Content-Type': 'application/octet-stream' },
        body: blob,
        ...(signal ? { signal } : {}),
      });
      if (response.ok) { return response.json(); }
      lastError = await failure(response, 'A part of the zip could not be sent');
      if (response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429) { throw lastError; }
    } catch (err) {
      if (err.name === 'AbortError' || signal?.aborted) { throw abortError(); }
      if (err === lastError && err.status && err.status < 500 && err.status !== 408 && err.status !== 429) { throw err; }
      lastError = err;
    }
    if (attempt < waits.length) { await wait(waits[attempt], signal); }
  }
  throw lastError;
}

/**
 * Sends `file` (a File or Blob, never read whole): POST /uploads `{ filename, size }`, then each chunk of the size the
 * gateway names as PUT /uploads/:id/parts/:n (application/octet-stream), one after the other. `onStart(id)` once the
 * upload exists (so it can be cancelled), `onProgress({ sent, total })` after each chunk. Resolves the upload's id.
 */
export async function startZipUpload(file, { onStart, onProgress, signal, waits } = {}) {
  const created = await call('/uploads', { method: 'POST', body: { filename: file.name || 'upload.zip', size: file.size } });
  const { id, chunkBytes } = created.upload;
  if (onStart) { onStart(id); }
  const total = file.size;
  const parts = Math.max(1, Math.ceil(total / chunkBytes));
  for (let number = 1; number <= parts; number += 1) {
    const start = (number - 1) * chunkBytes;
    const end = Math.min(total, number * chunkBytes);
    await sendChunk(id, number, file.slice(start, end), { signal, waits });
    if (onProgress) { onProgress({ sent: end, total }); }
  }
  return id;
}

/** The upload is complete: the gateway checks the zip. The same as getMyBackups, with `run` (the uploaded backup). */
export function completeZipUpload(id) {
  return call(`/uploads/${seg(id)}/complete`, { method: 'POST', body: {} });
}

export function cancelZipUpload(id) {
  return call(`/uploads/${seg(id)}`, { method: 'DELETE' });
}
